import type { ApiCartException, ApiException } from '../../exceptions/exceptionDefinitions';
import type { AxiosException } from './localApiAxiosErrorInterceptors';

import { createDefaultException } from '../../exceptions/exceptionUtils';

export const TIME_WINDOW_DAILY_ORDER_LIMIT_REACHED_MESSAGE =
    'Din organisation har nått butikens dagliga ordergräns för vald dag.';

export const mapHybrisBaseErrorToException = (
    hybrisError: ApiHybrisBaseError,
): ApiException | null => {
    if (hybrisError.type === 'CartError') {
        return <ApiCartException>{
            type: 'Cart',
            code: hybrisError.reason,
            friendlyMessage: 'Det gick inte att ladda varukorgen.',
        };
    }
    if (hybrisError.type === 'StoreNotFoundError') {
        return <ApiCartException>{
            type: 'Cart',
            code: 'StoreNotFound',
            friendlyMessage: 'Kunde inte hitta butiken.',
        };
    }
    if (
        hybrisError.type === 'ValidationError' &&
        hybrisError.subjectType === 'Cart' &&
        hybrisError.reason === 'TIME_WINDOW_NOT_VALID'
    ) {
        return <ApiCartException>{
            type: 'Cart',
            code: hybrisError.reason,
            friendlyMessage:
                'Din reserverade tid är tyvärr ej längre tillgänglig. Varorna finns kvar i din varukorg, så reservera gärna en ny tid och försök igen.',
        };
    }

    if (
        hybrisError.type === 'ValidationError' &&
        hybrisError.subjectType === 'Cart' &&
        hybrisError.reason === 'TOTAL_NOT_VALID'
    ) {
        return <ApiCartException>{
            type: 'Cart',
            code: hybrisError.reason,
            friendlyMessage:
                'Av någon anledning är ditt ordervärde för lågt för att lägga en order. Fyll gärna på med något mer du behöver för att uppnå gränsen!',
        };
    }

    if (hybrisError.reason === 'TIME_WINDOW_DAILY_ORDER_LIMIT_REACHED') {
        return <ApiCartException>{
            type: 'Cart',
            code: hybrisError.reason,
            friendlyMessage: TIME_WINDOW_DAILY_ORDER_LIMIT_REACHED_MESSAGE,
        };
    }

    return null;
};

export const hybrisErrorInterceptor = <TException extends ApiHybrisErrors>(
    error: AxiosException<HybrisApiError<TException>>,
) => {
    if (!Array.isArray(error?.response.data.errors)) {
        return null;
    }

    const hybrisErrors = error.response.data.errors;
    for (let i = 0; i < hybrisErrors.length; i += 1) {
        const hybrisError = hybrisErrors[i];
        if (isApiHybrisBaseError(hybrisError)) {
            const mappedException = mapHybrisBaseErrorToException(hybrisError);
            if (mappedException) {
                return mappedException;
            }
        }
    }

    return createDefaultException();
};

export interface HybrisApiError<T> {
    errors: T[];
}

export interface ApiHybrisBaseError {
    message: string;
    reason: string;
    subjectType: string;
    type: string;
}

export interface ApiHybrisValidationError extends ApiHybrisBaseError {
    subject: string;
}

export interface ApiValidateCartError {
    errorCode: string;
    errorMessage: string;
}

export const isApiHybrisBaseError = (error: unknown): error is ApiHybrisBaseError => {
    return (error as ApiHybrisBaseError)?.type !== undefined;
};

export type ApiHybrisErrors = ApiHybrisBaseError | ApiValidateCartError | ApiHybrisValidationError;
